import { useCallback, useEffect, useState } from "react";
import { useOrgAdminClient } from "./useOrgAdminClient.js";
export function useUsers(params) {
    const client = useOrgAdminClient();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await client.listUsers({
                status: params?.status || undefined,
                role: params?.role || undefined,
                limit: 100,
            });
            setUsers(res.data);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load users");
        }
        finally {
            setLoading(false);
        }
    }, [client, params?.status, params?.role]);
    useEffect(() => {
        reload();
    }, [reload]);
    const createUser = useCallback(async (input) => {
        await client.createUser(input);
        await reload();
    }, [client, reload]);
    const deleteUser = useCallback(async (userId) => {
        await client.deleteUser(userId);
        await reload();
    }, [client, reload]);
    const updateUser = useCallback(async (userId, patch) => {
        // Return the response — the caller MUST be able to check what the
        // server actually applied (an older build silently drops `email`)
        // and to surface the re-issued invitation URL.
        const res = await client.updateUser(userId, patch);
        await reload();
        return res.data;
    }, [client, reload]);
    const resendInvitation = useCallback(async (userId) => {
        // Return the response so the caller can surface the fresh
        // invitation URL (the create POST never returns it — it's
        // generated inside the async pipeline; resend is the only path
        // that returns it synchronously).
        const res = await client.resendInvitation(userId);
        await reload();
        return res.data;
    }, [client, reload]);
    const revokeInvitation = useCallback(async (userId) => {
        await client.revokeInvitation(userId);
        await reload();
    }, [client, reload]);
    const acceptInvitation = useCallback(async (userId) => {
        await client.acceptInvitation(userId);
        await reload();
    }, [client, reload]);
    return {
        users,
        loading,
        error,
        reload,
        createUser,
        deleteUser,
        resendInvitation,
        revokeInvitation,
        acceptInvitation,
    };
}
//# sourceMappingURL=useUsers.js.map